import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createTicket, getCustomerProfile } from "../../api";
import styles from "./CreateTicket.module.css";
import validateForm from "../../utils/validateForm";

const CreateTicket = () => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    subject: "",
    issueType: "",
    otherIssue: "",
    priorityLevel: "",
    description: "",
    attachment: null,
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getCustomerProfile();
        const profile = response.data;
        setFormData((prevFormData) => ({
          ...prevFormData,
          name: `${profile.first_name} ${profile.last_name}`.trim(),
          phoneNumber: profile.phone,
          email: profile.email,
        }));
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      try {
        const dataToSend = {
          subject: formData.subject,
          issue_type: formData.issueType,
          custom_issue_type:
            formData.issueType === "Other" ? formData.otherIssue : "",
          priority_level: formData.priorityLevel,
          description: formData.description,
          attachment_url: formData.attachment || "",
        };
        await createTicket(dataToSend);
        setApiError("");
        setSuccessMessage(
          "✅ Ticket submitted successfully! Our support team will get back to you soon."
        );
      } catch (error) {
        console.error("Error submitting ticket:", error);
        setApiError(
          error.response?.data?.error ||
            "We couldn’t submit your ticket. Please try again in a moment."
        );
      }
    } else {
      setApiError("⚠️ Please fix the highlighted errors before submitting.");
      setSuccessMessage("");
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <div className={styles.container}>
      <h2>Support Ticket</h2>
      {apiError && <div className={styles.apiError}>{apiError}</div>}
      {successMessage && (
        <div className={styles.successMessage}>{successMessage}</div>
      )}{" "}
      <form onSubmit={handleSubmit}>
        <div className={styles.row}>
          <div>
            <input
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name}
              onChange={handleChange}
              className={`${styles.input} ${
                errors.name ? styles.errorBorder : ""
              }`}
              readOnly
            />
            {errors.name && <span className={styles.error}>{errors.name}</span>}
          </div>
          <div>
            <input
              type="text"
              name="subject"
              placeholder="Subject"
              value={formData.subject}
              onChange={handleChange}
              className={`${styles.input} ${
                errors.subject ? styles.errorBorder : ""
              }`}
            />
            {errors.subject && (
              <span className={styles.error}>{errors.subject}</span>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div>
            <input
              type="text"
              name="phoneNumber"
              placeholder="Phone Number"
              value={formData.phoneNumber}
              onChange={handleChange}
              className={`${styles.input} ${
                errors.phoneNumber ? styles.errorBorder : ""
              }`}
              readOnly
            />
            {errors.phoneNumber && (
              <span className={styles.error}>{errors.phoneNumber}</span>
            )}
          </div>
          <div>
            <select
              name="issueType"
              value={formData.issueType}
              onChange={handleChange}
              className={`${styles.select} ${
                errors.issueType ? styles.errorBorder : ""
              }`}
            >
              <option value="" disabled>
                Issue Type
              </option>
              <option value="Technical Issue">Technical Issue</option>
              <option value="Billing/Account Issue">
                Billing/Account Issue
              </option>
              <option value="Feature Request">Feature Request</option>
              <option value="General Inquiry">General Inquiry</option>
              <option value="Other">Other (Please Specify)</option>
            </select>
            {errors.issueType && (
              <span className={styles.error}>{errors.issueType}</span>
            )}
          </div>
        </div>

        {formData.issueType === "Other" && (
          <div className={styles.otherIssueContainer}>
            <input
              type="text"
              name="otherIssue"
              placeholder="Please specify the issue"
              value={formData.otherIssue}
              onChange={handleChange}
              className={`${styles.otherIssueInput} ${
                errors.otherIssue ? styles.errorBorder : ""
              }`}
            />
            {errors.otherIssue && (
              <span className={styles.error}>{errors.otherIssue}</span>
            )}
          </div>
        )}

        <div className={styles.row}>
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email Id"
              value={formData.email}
              onChange={handleChange}
              className={`${styles.input} ${
                errors.email ? styles.errorBorder : ""
              }`}
              readOnly
            />
            {errors.email && (
              <span className={styles.error}>{errors.email}</span>
            )}
          </div>
          <div>
            <select
              name="priorityLevel"
              onChange={handleChange}
              className={`${styles.select} ${
                errors.priorityLevel ? styles.errorBorder : ""
              }`}
              defaultValue=""
            >
              <option value="" disabled>
                Priority Level
              </option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
            {errors.priorityLevel && (
              <span className={styles.error}>{errors.priorityLevel}</span>
            )}
          </div>
        </div>

        <div className={styles.textareaContainer}>
          <textarea
            name="description"
            placeholder="Description"
            value={formData.description}
            onChange={handleChange}
            className={`${styles.textarea} ${
              errors.description ? styles.errorBorder : ""
            }`}
          ></textarea>
          {errors.description && (
            <span className={styles.error}>{errors.description}</span>
          )}
        </div>

        <div className={styles.row}>
          <label className={styles.label}>
            <strong>Attach Files</strong> (Optional):
            <input
              type="file"
              name="attachment"
              onChange={handleChange}
              className={styles.fileInput}
            />
          </label>
        </div>

        <div className={styles.buttonGroup}>
          <button type="submit" className={styles.uploadButton}>
            Submit
          </button>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBack}
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTicket;
